import { AppShell } from '@/components/layout/AppShell';
import { Hero } from '@/features/home/Hero';
import { Alert, Badge, Card, DataRow, ErrorState, Skeleton, SkeletonGroup } from '@/components/ui';
import { useMeta } from '@/lib/api/meta';
import { useAnnounceOnChange } from '@/lib/a11y';
import { useOnlineStatus } from '@/lib/hooks/useOnlineStatus';
import {
  formatBps,
  formatCount,
  formatExtensionList,
  formatMegabytes,
  formatPaise,
  plural,
} from '@/lib/format';
import { Link } from 'react-router-dom';

import { paths } from '@/routes/paths';
import type { MetaResponse } from '@/lib/api/types';

/**
 * The home page, and the milestone-1 proof that the whole stack works:
 * browser -> vite dev server -> API (CORS + x-request-id) -> back into the UI.
 *
 * It renders GET /api/v1/meta as a customer-facing page, not a JSON dump. The
 * distinction matters: the point of milestone 1 is a screen we could put in front
 * of a customer with the ordering flow attached, which forces the loading, error,
 * offline and empty paths to be designed rather than discovered later.
 *
 * All four async states are explicit and separately testable:
 *   - pending  -> skeletons shaped like the eventual content
 *   - error    -> ErrorState with the server's message and a working retry
 *   - offline  -> a banner from the shell plus an offline-specific ErrorState
 *   - success  -> the real content
 */
export function HomePage() {
  const online = useOnlineStatus();
  const { data: meta, isPending, isError, error, refetch, isFetching } = useMeta();

  // Announce the settled outcome once, politely. A sighted user sees the skeletons
  // resolve; without this a screen-reader user gets no signal that anything landed.
  useAnnounceOnChange(
    isError
      ? `Couldn't load service details. ${error.message}`
      : meta
        ? 'Service details loaded.'
        : null,
    isError ? 'assertive' : 'polite',
  );

  return (
    // `titleHidden`: the hero's own <h1> is the page heading. Rendering the
    // shell's title as well would give the page two h1s and read the offer
    // twice to a screen reader.
    // `wide`: the home page is presentation, not a form, so the reading-width
    // cap that protects the checkout would only strand the hero in a column.
    <AppShell title="Printoman — your 24/7 print hero" titleHidden wide>
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        {/* A real link, not navigate() — long-press and open-in-new-tab keep
            working. Disabled offline: starting an order needs the API, and a
            dead tap is worse than a clear "no". */}
        <div className="lg:col-span-2">
          <Hero
            online={online}
            acceptingOrders={meta?.accepting_orders ?? true}
            closedMessage={meta?.closed_message}
          />
        </div>
        {/* A returning customer's way back in. Secondary to starting an order,
            but it must exist somewhere findable — otherwise the only route to a
            placed order is still having its number in your history. */}
        <p className="text-sm text-muted lg:col-span-2">
          Already ordered?{' '}
          <Link
            to={paths.orders}
            className="font-medium text-brand-ink underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            Track your orders
          </Link>
        </p>

        {isPending ? <MetaSkeleton /> : null}

        {isError ? (
          <ErrorState
            error={error}
            onRetry={() => void refetch()}
            retrying={isFetching}
            title={error.isOffline ? "You're offline" : 'We couldn’t load the service details'}
          />
        ) : null}

        {meta ? <MetaContent meta={meta} /> : null}
      </div>
    </AppShell>
  );
}

/**
 * Skeleton shaped like MetaContent — same card count, same row heights. A skeleton
 * that does not match its content just moves the layout jump later.
 */
function MetaSkeleton() {
  return (
    <SkeletonGroup label="Loading service details" className="flex flex-col gap-4">
      <div className="rounded-card border border-line bg-surface p-4 shadow-card">
        <Skeleton className="h-4 w-24" />
        <div className="mt-4 flex flex-col gap-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      </div>
      <div className="rounded-card border border-line bg-surface p-4 shadow-card">
        <Skeleton className="h-4 w-32" />
        <div className="mt-4 flex flex-col gap-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
      <div className="rounded-card border border-line bg-surface p-4 shadow-card">
        <Skeleton className="h-4 w-28" />
        <div className="mt-4 flex flex-wrap gap-2">
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-7 w-16" />
        </div>
      </div>
    </SkeletonGroup>
  );
}

function MetaContent({ meta }: { meta: MetaResponse }) {
  const isProduction = meta.env === 'production';
  const freeShipping = meta.pricing.free_shipping_threshold_paise;

  return (
    <>
      {/* Non-production environments say so, loudly. A staging build that looks
          identical to production is how test orders end up in the real queue. */}
      {!isProduction ? (
        // Spans both desktop columns: a warning that only covers half the page
        // reads as a note about the panel beside it, not about the whole site.
        <Alert severity="warning" title={`${meta.env} environment`} className="lg:col-span-2">
          <p>
            This is not the live service. Orders placed here are not printed
            {meta.debug_expose_otp
              ? ', and sign-in codes are shown on screen instead of texted'
              : ''}
            .
          </p>
        </Alert>
      ) : null}

      <Card
        title="What you can print"
        aside={<Badge tone="brand">{meta.pricing.pricing_version}</Badge>}
      >
        <dl>
          <DataRow label="File type" value={formatExtensionList(meta.upload.accepted_extensions)} />
          <DataRow label="Maximum file size" value={formatMegabytes(meta.upload.max_upload_mb)} />
          {/* 0 means the shop has turned the page warning off. Rendering the
              row anyway would read "0 pages", which says the opposite. */}
          {meta.upload.warn_page_count > 0 ? (
            <DataRow
              label="Page limit before a manual check"
              value={plural(meta.upload.warn_page_count, 'page')}
            />
          ) : (
            <DataRow label="Page count" value="No limit" />
          )}
        </dl>
        <p className="mt-3 text-sm text-ink-subtle">
          Bigger or longer documents are still welcome &mdash; we&rsquo;ll flag anything that needs
          a closer look before printing.
        </p>
      </Card>

      <Card title="Paper sizes">
        {meta.paper_sizes.length > 0 ? (
          <>
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver drops list semantics when list-style is none; see Stepper. */}
            <ul className="flex list-none flex-wrap gap-2" role="list">
              {meta.paper_sizes.map((size) => (
                <li key={size}>
                  <Badge tone="neutral" className="px-3 py-1 text-sm">
                    {size}
                  </Badge>
                </li>
              ))}
            </ul>

            {/* Named rather than omitted. A customer whose document is A3 needs
                to know it is coming, not be left guessing whether we simply
                forgot to list it — and silence reads as "never". */}
            {meta.paper_sizes_coming_soon.length > 0 ? (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  Available soon
                </p>
                {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- see above */}
                <ul className="mt-2 flex list-none flex-wrap gap-2" role="list">
                  {meta.paper_sizes_coming_soon.map((size) => (
                    <li key={size}>
                      <Badge tone="neutral" className="border-dashed px-3 py-1 text-sm opacity-60">
                        {size}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          // Empty is a real state, not an error: the server can legitimately
          // return an empty list while the catalogue is being edited.
          <p className="text-sm text-ink-muted">
            Paper sizes are being updated. Start an order and we&rsquo;ll show what&rsquo;s
            available.
          </p>
        )}
      </Card>

      <Card title="Pricing and tax">
        <dl>
          <DataRow label="Currency" value={meta.pricing.currency} />
          <DataRow
            label={meta.pricing.tax_label}
            value={`${formatBps(meta.pricing.tax_rate_bps)} on the printing total`}
          />
          <DataRow
            label="Price held for"
            value={plural(meta.pricing.quote_ttl_minutes, 'minute')}
          />
          {freeShipping > 0 ? (
            <DataRow label="Free delivery over" value={formatPaise(freeShipping)} />
          ) : null}
        </dl>
        <p className="mt-3 text-sm text-ink-subtle">
          You see the full total &mdash; printing, {meta.pricing.tax_label} and delivery &mdash;
          before you pay. Prices are held for {formatCount(meta.pricing.quote_ttl_minutes)} minutes
          while you check out.
        </p>
      </Card>

      <Card title="How it works">
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- see above */}
        <ol className="flex list-none flex-col gap-3" role="list">
          {[
            { step: 'Upload', text: 'Send us your PDF. We check it and tell you what we found.' },
            { step: 'Choose', text: 'Paper, colour, sides and binding — the price updates live.' },
            { step: 'Pay', text: 'Confirm the total and pay. We print and deliver.' },
          ].map((item, index) => (
            <li key={item.step} className="flex gap-3">
              <span
                aria-hidden="true"
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand-ink"
              >
                {index + 1}
              </span>
              <span className="text-sm">
                <span className="font-semibold text-ink">{item.step}.</span>{' '}
                <span className="text-ink-muted">{item.text}</span>
              </span>
            </li>
          ))}
        </ol>
      </Card>

      <p className="px-1 text-center text-xs text-ink-subtle">
        {meta.service} &middot; all times shown in {meta.timezone.replace('_', ' ')}
      </p>
    </>
  );
}
